import { makeAutoObservable } from "mobx";
import SearchState, { API_BASE_URL } from "../../SearchState";
import retryFetch from "../../utils/retryFetch";

/** @typedef {import("@shared/authEndpointDatatypes").ChangePasswordPost} ChangePasswordPost */
/** @typedef {import("@shared/authEndpointDatatypes").ChangePasswordPostResponse} ChangePasswordPostResponse */
/** @typedef {import("@shared/authEndpointDatatypes").CreateUserPost} CreateUserPost */
/** @typedef {import("@shared/authEndpointDatatypes").CreateUserPostResponse} CreateUserPostResponse */
/** @typedef {import("@shared/authEndpointDatatypes").ResetPasswordPost} ResetPasswordPost */
/** @typedef {import("@shared/authEndpointDatatypes").ResetPasswordPostResponse} ResetPasswordPostResponse */
/** @typedef {import("@shared/authEndpointDatatypes").SessionStatusResponse} SessionStatusResponse */

export default class LoginFlowState {
  /** @type {SearchState} */
  searchState;

  /** @type {"login" | "forgot" | "forgot-success" | "change-password" | "change-success" | "create" | "create-success"} */
  currentPage = "login";

  /** @type {string | null} */
  passwordResetLink = null;

  /**
   * @param {SearchState} searchState
   */
  constructor(searchState) {
    makeAutoObservable(this, {}, { enforceActions: "observed" });
    this.searchState = searchState;
  }

  backToLogin = () => {
    this.currentPage = "login";
  };

  toForgotPassword = () => {
    this.currentPage = "forgot";
  };

  toCreateAccount = () => {
    this.currentPage = "create";
    this.lastCreateFailed = false;
  };

  close = () => {
    this.searchState.hideLoginFlow();

    this.currentPage = "login";
    this.loginEmail = "";
    this.loginPassword = "";
    this.lastLoginFailed = false;
    this.createEmail = "";
    this.createPassword = "";
    this.createRepeatPassword = "";
  };

  // Login page state

  loginEmail = "";

  loginPassword = "";

  // 'testing';
  lastLoginFailed = false;

  /**
   * Sets the login email
   * @param {string} email
   * @action
   */
  setLoginEmail = (email) => {
    this.loginEmail = email;
  };

  /**
   * Sets the login password
   * @param {string} password
   * @action
   */
  setLoginPassword = (password) => {
    this.loginPassword = password;
  };

  login = () => {
    retryFetch(`${API_BASE_URL}/api/v1/auth/local`, {
      method: "POST",
      body: JSON.stringify({
        email: this.loginEmail,
        password: this.loginPassword,
      }),
      headers: new Headers({ "content-type": "application/json" }),
      mode: "cors",
      redirect: "error",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.loggedIn) {
          this.searchState.setLoggedInUser(r.email, r.isAdmin);
          this.close();
          this.searchState.showMyAccount();
          this.searchState.logEvent("account", "logged-in", ``);
        } else {
          this.lastLoginFailed = true;
          this.searchState.logEvent("account", "login-failed", ``);
        }
      });
  };

  // Create account state

  createEmail = "";

  createPassword = "";

  createRepeatPassword = "";

  lastCreateFailed = false;

  showingPrivacyPolicy = false;

  createAccountPending = false;

  /**
   * Sets the email for account creation
   * @param {string} email
   * @action
   */
  setCreateEmail = (email) => {
    this.createEmail = email;
  };

  /**
   * Sets the password for account creation
   * @param {string} password
   * @action
   */
  setCreatePassword = (password) => {
    this.createPassword = password;
  };

  /**
   * Sets the repeat password for account creation
   * @param {string} password
   * @action
   */
  setCreateRepeatPassword = (password) => {
    this.createRepeatPassword = password;
  };

  showPrivacyPolicy = () => {
    this.showingPrivacyPolicy = true;
  };

  createAccount = () => {
    /** @type {CreateUserPost} */
    const createUserPost = {
      email: this.createEmail,
      password: this.createPassword,
    };
    this.createAccountPending = true;

    retryFetch(`${API_BASE_URL}/api/v1/auth/create_user`, {
      method: "POST",
      body: JSON.stringify(createUserPost),
      headers: new Headers({ "content-type": "application/json" }),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        this.createAccountPending = false;
        if (r.success) {
          this.searchState.setLoggedInUser(this.createEmail);
          this.currentPage = "create-success";
          this.searchState.logEvent("account", "create-account-success", ``);
        } else {
          // r is "error creating account"
          console.log(r);
          this.lastCreateFailed = true;
          this.searchState.logEvent("account", "create-account-failed", ``);
        }
      });
  };

  changePassword = () => {
    /** @type {ChangePasswordPost} */
    const changePasswordPost = {
      passwordResetLink: this.passwordResetLink,
      password: this.createPassword,
    };

    retryFetch(`${API_BASE_URL}/api/v1/auth/change_password`, {
      method: "POST",
      body: JSON.stringify(changePasswordPost),
      headers: new Headers({ "content-type": "application/json" }),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.success) {
          if (r.sessionStatus != null) {
            this.searchState.setLoggedInUser(
              r.sessionStatus.email,
              r.sessionStatus.isAdmin,
            );
            this.currentPage = "change-success";
          }
        } else {
          // TODO: show the error
          console.log(r);
        }
      });
  };

  resetPassword = () => {
    /** @type {ResetPasswordPost} */
    const resetPasswordPost = {
      email: this.loginEmail,
      currentLocation: window.location.href,
    };

    retryFetch(`${API_BASE_URL}/api/v1/auth/reset_password`, {
      method: "POST",
      body: JSON.stringify(resetPasswordPost),
      headers: new Headers({ "content-type": "application/json" }),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.success) {
          this.currentPage = "forgot-success";
        } else {
          alert("That user does not exist");
          // TODO: deal with the error case here
        }
      });
  };
}
